import React, { FC, useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { User } from 'models/user';
import { useQuery } from '@apollo/client';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import useBus from 'use-bus';

const filterNew = createFilterOptions<User>();

type ApprovedByAutocompleteProps = {
    onTechnicianSelected: (technician: User | null) => void;
    labTechnician?: User | null;
    dataTest?: string;
};

const ApprovedByAutocomplete: FC<ApprovedByAutocompleteProps> = ({ onTechnicianSelected, labTechnician, dataTest }) => {
    const { t: translate } = useTranslation(['common']);
    const [technicians, setTechnicians] = useState<User[]>([]);
    const [technician, setTechnician] = useState<User | null>(null);
    const currentOrg = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const [inputValue, setInputValue] = useState('');

    useQuery(getTechniciansForUserListTemplate, {
        variables: {
            labId: currentOrg._id,
            filters: { active: true, role: 'technician' },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => {
            setTechnicians(technicians);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setTechnician(labTechnician || null);
        setInputValue(labTechnician?.fullName || '');
    }, [labTechnician]);

    const onTechnicianSelect = (_: React.SyntheticEvent, value: User | string | null) => {
        if (typeof value === 'string') {
            const newUser = {
                fullName: value,
            } as User;
            setTechnician(newUser);
            setInputValue(value);
            onTechnicianSelected(newUser);
            return;
        }
        if (!value) {
            setTechnician(null);
            setInputValue('');
            onTechnicianSelected({} as User);
            return;
        }
        setTechnician(value);
        setInputValue(value.fullName || '');
        onTechnicianSelected(value);
    };

    useBus(
        'qr-select-technician',
        ({ payload }) => {
            if (payload.technicianId && technicians.length > 0) {
                const qrTech = technicians.find((t) => t._id === payload.technicianId);

                if (qrTech) {
                    setTechnician(qrTech);
                    setInputValue(qrTech.fullName || '');
                    onTechnicianSelected(qrTech);
                }
            }
        },
        [technicians],
    );

    return (
        <Autocomplete
            options={technicians}
            value={technician}
            inputValue={inputValue}
            freeSolo
            autoSelect
            blurOnSelect
            onChange={onTechnicianSelect}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);

                // When cleared via backspace
                if (!newInputValue) {
                    setTechnician(null);
                    onTechnicianSelected({} as User);
                }
            }}
            isOptionEqualToValue={(option, value) => option?._id === value?._id}
            getOptionLabel={(option: User | string) => {
                if (typeof option === 'string') return option;
                if ((option as any).inputValue) {
                    return (option as any).inputValue;
                }
                return option.fullName || '';
            }}
            filterOptions={(options, params) => {
                const filtered = filterNew(options, params);
                const { inputValue } = params;
                const isExisting = options.some((option) => inputValue === (option.fullName || ''));
                if (inputValue !== '' && !isExisting) {
                    filtered.push({
                        inputValue,
                        fullName: inputValue,
                    } as User);
                }
                return filtered;
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={translate('common:TECHNICIANS')}
                    margin="dense"
                    fullWidth
                    autoFocus
                    data-test={dataTest || 'approved-by-autocomplete.autocomplete'}
                    InputProps={{ ...params.InputProps, type: 'text' }}
                />
            )}
        />
    );
};

export default ApprovedByAutocomplete;
